"use client";

import { useMemo } from "react";
import { Check, Minus, Plus, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { cn } from "~/lib/utils";
import {
  LOCATION_MONTHLY_CENTS,
  MARKETPLACE_COMMUNICATION_OPTIONS,
  MARKETPLACE_HIRE_PLANS,
  MARKETPLACE_SCHEDULING_INTEGRATIONS,
  TEAM_MEMBER_MONTHLY_CENTS,
  type HirePlanSelection,
  type MarketplaceCommunicationOptionId,
  type MarketplaceHirePlanId,
  type MarketplaceHireTool,
  computeHireTotalCents,
  computeLocationCostCents,
  computeTeamMemberCostCents,
  defaultHirePlanSelection,
  formatHirePrice,
  formatHireTotalPrice,
  formatPlanDescription,
  formatPlanTabPrice,
  getHirePlan,
} from "~/lib/marketplace/hire-pricing";
import { HIRE_AGENT_DETAILS_CHECKLIST } from "~/lib/marketplace/hire-template-defaults";

type HirePlanSelectionStepProps = {
  agentName: string;
  selection: HirePlanSelection;
  onChange: (next: HirePlanSelection) => void;
  className?: string;
};

function ChecklistSection({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <h4 className="mb-2 text-sm font-semibold text-slate-900">{title}</h4>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ToolsSection({ title, items }: { title: string; items: MarketplaceHireTool[] }) {
  return (
    <div>
      <h4 className="mb-2 text-sm font-semibold text-slate-900">{title}</h4>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li
            key={item.label}
            className={cn(
              "flex items-start gap-2 text-sm",
              item.included ? "text-slate-700" : "text-slate-500",
            )}
          >
            {item.included ? (
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
            ) : (
              <X className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden />
            )}
            <span>{item.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function HirePlanSelectionStep({
  agentName,
  selection,
  onChange,
  className,
}: HirePlanSelectionStepProps) {
  const plan = useMemo(() => getHirePlan(selection.planId), [selection.planId]);
  const teamMemberCostCents = useMemo(
    () => computeTeamMemberCostCents(plan, selection.teamMemberCount),
    [plan, selection.teamMemberCount],
  );
  const locationCostCents = useMemo(
    () => computeLocationCostCents(plan, selection.locationCount),
    [plan, selection.locationCount],
  );
  const totalCents = useMemo(() => computeHireTotalCents(selection), [selection]);

  const selectPlan = (nextPlanId: MarketplaceHirePlanId) => {
    onChange(defaultHirePlanSelection(nextPlanId));
  };

  const updateSelection = (patch: Partial<HirePlanSelection>) => {
    onChange({ ...selection, ...patch });
  };

  const toggleCommunication = (id: MarketplaceCommunicationOptionId, checked: boolean) => {
    if (plan.includedCommunication.includes(id)) return;
    updateSelection({
      communicationOptionIds: checked
        ? [...selection.communicationOptionIds, id]
        : selection.communicationOptionIds.filter((x) => x !== id),
    });
  };

  return (
    <section className={cn("space-y-5", className)}>
      <div>
        <h3 className="text-sm font-semibold text-foreground">Select plan</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Choose the plan that fits your business. You can adjust team size and integrations below.
        </p>
      </div>

      <div className="grid grid-cols-3 overflow-hidden rounded-lg border border-slate-200">
        {MARKETPLACE_HIRE_PLANS.map((tier) => {
          const active = tier.id === selection.planId;
          const tabPrice = formatPlanTabPrice(tier.priceCents);
          return (
            <button
              key={tier.id}
              type="button"
              onClick={() => selectPlan(tier.id)}
              className={cn(
                "flex flex-col items-center justify-center px-2 py-3 text-center transition-colors",
                active
                  ? "bg-primary/10 text-primary"
                  : "bg-white text-slate-600 hover:bg-slate-50",
              )}
            >
              <span className="text-xs font-semibold">{tier.label}</span>
              <span className="text-sm font-bold leading-tight">{tabPrice.main}</span>
              {tabPrice.sub ? (
                <span className="text-[10px] font-medium opacity-80">{tabPrice.sub}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="rounded-md border border-slate-100 bg-slate-50/80 px-4 py-3">
        <div className="flex items-baseline justify-between gap-2">
          <div>
            <p className="text-sm font-bold text-slate-900">{plan.label}</p>
            <p className="text-xs font-medium text-primary">{plan.subtitle}</p>
          </div>
          <p className="text-lg font-bold text-slate-900">{formatHireTotalPrice(totalCents)}</p>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          {formatPlanDescription(plan, agentName)}
        </p>
      </div>

      <ChecklistSection title="Skills" items={plan.skills} />
      <ChecklistSection
        title="Agent Details (step 3)"
        items={[...HIRE_AGENT_DETAILS_CHECKLIST]}
      />
      <ToolsSection title="Tools" items={plan.tools} />

      <div>
        <h4 className="mb-2 text-sm font-semibold text-slate-900">Appointments and Scheduling</h4>
        <Select
          value={selection.schedulingIntegrationId}
          onValueChange={(next) =>
            updateSelection({
              schedulingIntegrationId: next as HirePlanSelection["schedulingIntegrationId"],
            })
          }
        >
          <SelectTrigger className="h-10 w-full text-sm">
            <SelectValue placeholder="Select an integration" />
          </SelectTrigger>
          <SelectContent>
            {MARKETPLACE_SCHEDULING_INTEGRATIONS.map((integration) => (
              <SelectItem key={integration.id} value={integration.id}>
                {integration.id === "none" ? "None" : integration.label} (
                {formatHirePrice(integration.priceCents)}/month)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {plan.showTeamMembersCalculator ? (
        <div>
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h4 className="text-sm font-semibold text-slate-900">Team Members</h4>
            <span className="text-xs text-slate-500">
              {formatHirePrice(TEAM_MEMBER_MONTHLY_CENTS)}/member/mo
            </span>
          </div>
          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() =>
                  updateSelection({
                    teamMemberCount: Math.max(1, selection.teamMemberCount - 1),
                  })
                }
                disabled={selection.teamMemberCount <= 1}
                aria-label="Decrease team members"
              >
                <Minus className="h-3.5 w-3.5" />
              </Button>
              <span className="min-w-[2rem] text-center text-sm font-semibold tabular-nums">
                {selection.teamMemberCount}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => updateSelection({ teamMemberCount: selection.teamMemberCount + 1 })}
                aria-label="Increase team members"
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
            <span className="text-sm font-medium text-slate-700">
              {teamMemberCostCents === 0
                ? "Included"
                : `${formatHirePrice(teamMemberCostCents)}/mo`}
            </span>
          </div>
        </div>
      ) : null}

      {plan.showLocationsCalculator ? (
        <div>
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h4 className="text-sm font-semibold text-slate-900">Locations</h4>
            <span className="text-xs text-slate-500">
              {formatHirePrice(LOCATION_MONTHLY_CENTS)}/location/mo
            </span>
          </div>
          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() =>
                  updateSelection({
                    locationCount: Math.max(1, selection.locationCount - 1),
                  })
                }
                disabled={selection.locationCount <= 1}
                aria-label="Decrease locations"
              >
                <Minus className="h-3.5 w-3.5" />
              </Button>
              <span className="min-w-[2rem] text-center text-sm font-semibold tabular-nums">
                {selection.locationCount}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => updateSelection({ locationCount: selection.locationCount + 1 })}
                aria-label="Increase locations"
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
            <span className="text-sm font-medium text-slate-700">
              {locationCostCents === 0
                ? "Included"
                : `${formatHirePrice(locationCostCents)}/mo`}
            </span>
          </div>
        </div>
      ) : null}

      {plan.showCommunicationSection ? (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-slate-900">Communication</h4>
          <div className="space-y-2">
            {MARKETPLACE_COMMUNICATION_OPTIONS.map((option) => {
              const isIncluded = plan.includedCommunication.includes(option.id);
              const checked = selection.communicationOptionIds.includes(option.id);
              return (
                <label
                  key={option.id}
                  className={cn(
                    "flex items-start gap-3 rounded-md border border-slate-100 p-3",
                    isIncluded ? "bg-slate-50/80" : "cursor-pointer hover:bg-slate-50/80",
                  )}
                >
                  <Checkbox
                    checked={checked}
                    disabled={isIncluded}
                    onCheckedChange={(v) => toggleCommunication(option.id, v === true)}
                    className="mt-0.5"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="text-sm font-medium text-slate-900">{option.label}</span>
                      <span className="text-sm font-semibold text-slate-700">
                        {isIncluded ? "Included" : `${formatHirePrice(option.priceCents)}/mo`}
                      </span>
                    </span>
                    <span className="mt-0.5 block text-xs leading-snug text-slate-500">
                      {option.description}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
}
